import { useCallback, useEffect, useState } from 'react';
import { useAppDialog } from 'shared/context/AppDialogContext';
import {
  setProductionVersion, revertVersion, listBackfills, confirmBackfill, cancelBackfill,
} from 'features/evals3/api/evals3Api';

/**
 * What can be done to an eval's versions, wherever the Versions panel is
 * open — the editor, or the results page. One implementation, because
 * "Set as production" is the one act that changes what runs, and it carries
 * a follow-up (re-scoring history under the new version, asked with an
 * estimate while the person is looking) that must not differ by page.
 *
 *   promote(version, {confirmed})   move the production label (TRO-461)
 *   restore(version, {discarding})  a NEW version equal to an old one; resolves to it
 *   backfills / confirm / cancel    re-scoring history, one per version
 *
 * `reload` refetches the lineage after a change; `onChanged` tells the list.
 */
export default function useVersionActions({ teamId, lineageId, target, reload, onChanged }) {
  const { confirm: confirmDialog, alert: alertDialog } = useAppDialog();
  const [acting, setActing] = useState(null);
  const [backfills, setBackfills] = useState([]);

  const refreshBackfills = useCallback(async () => {
    setBackfills(await listBackfills(teamId, lineageId).catch(() => []));
  }, [teamId, lineageId]);
  useEffect(() => { refreshBackfills(); }, [refreshBackfills]);

  /** The eval's backfill policy fired when the label moved. A costly one waits for a go-ahead. */
  const askBackfill = useCallback(async (bf) => {
    if (!bf || bf.status !== 'awaiting_confirmation') return;
    const e = bf.estimate || {};
    const spend = e.projected_provider_spend_usd
      ? `$${Number(e.projected_provider_spend_usd.min).toFixed(2)}–$${Number(e.projected_provider_spend_usd.max).toFixed(2)} provider spend`
      : 'provider spend not yet known (this eval has no judge history to price it from)';
    const go = await confirmDialog(
      `${bf.confirm_reason || ''} Estimated ${e.projected_units?.min ?? '?'}–${e.projected_units?.max ?? '?'} units and ${spend}. You can also start it later from Versions.`,
      { title: `Re-score ${e.units_in_window} ${e.unit_word} under v${bf.version}?`, confirmLabel: 'Re-score history', cancelLabel: 'Not now' },
    );
    if (go) await confirmBackfill(teamId, bf.id);
    await refreshBackfills();
  }, [confirmDialog, teamId, refreshBackfills]);

  const promote = useCallback(async (version, { confirmed = false } = {}) => {
    if (!confirmed) {
      const ok = await confirmDialog(`Every new ${target || 'event'} is scored by v${version} from now on. The version in production today stays in the history.`,
        { title: `Set v${version} as production?`, confirmLabel: 'Set as production' });
      if (!ok) return;
    }
    setActing(`prod:${version}`);
    try {
      const out = await setProductionVersion(teamId, lineageId, version);
      await reload?.();
      onChanged?.();
      await askBackfill(out?.backfill);
    } catch (e) { alertDialog(e?.response?.data?.message || e?.response?.data?.error || e.message, { title: 'Could not set production' }); }
    finally { setActing(null); }
  }, [confirmDialog, target, teamId, lineageId, reload, onChanged, askBackfill, alertDialog]);

  /** Resolves to the new version number, or null when cancelled or refused. */
  const restore = useCallback(async (version, { discarding = false } = {}) => {
    const ok = await confirmDialog(`A new version equal to v${version} will be created. It does not run until you set it as production.${discarding ? ' Unsaved edits in the editor are discarded.' : ''}`,
      { title: `Restore v${version}?`, confirmLabel: 'Restore' });
    if (!ok) return null;
    setActing(version);
    try {
      const out = await revertVersion(teamId, lineageId, version);
      await reload?.();
      onChanged?.();
      return out?.definition?.version ?? null;
    } catch (e) { alertDialog(e?.response?.data?.error || e.message, { title: 'Could not restore' }); return null; }
    finally { setActing(null); }
  }, [confirmDialog, teamId, lineageId, reload, onChanged, alertDialog]);

  const confirm = useCallback(async (id) => {
    await confirmBackfill(teamId, id).catch((e) => alertDialog(e?.response?.data?.message || e.message, { title: 'Could not start' }));
    await refreshBackfills();
  }, [teamId, alertDialog, refreshBackfills]);

  const cancel = useCallback(async (id) => {
    await cancelBackfill(teamId, id).catch((e) => alertDialog(e?.response?.data?.message || e.message, { title: 'Could not cancel' }));
    await refreshBackfills();
  }, [teamId, alertDialog, refreshBackfills]);

  return { acting, backfills, refreshBackfills, promote, restore, confirmBackfill: confirm, cancelBackfill: cancel };
}
